import { Alert, theme as antdTheme } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { dashboardApi } from '@/api/dashboard';
import { useAuthStore } from '@/store/auth';
import { AdminDashboard } from './dashboards/admin';
import { AccountantDashboard } from './dashboards/accountant';
import { CashierDashboard } from './dashboards/cashier';
import { TeacherDashboard } from './dashboards/teacher';
import { FrontDeskDashboard } from './dashboards/frontdesk';
import { HrDashboard } from './dashboards/hr';
import { DashboardSkeleton } from './dashboards/shared';

/**
 * Dashboard dispatcher. Picks the per-role component based on the signed-in
 * user's role. All variants consume the same `/dashboard/summary` payload —
 * the backend BFF already returns role-aware data; the frontend just chooses
 * the right slice + framing.
 *
 * Unknown / unmapped roles fall through to the AdminDashboard which shows
 * the full set — that's a safe default for Super Admin and anyone we
 * haven't built a tailored variant for yet.
 */
export function DashboardPage() {
  const { token } = antdTheme.useToken();
  const role = useAuthStore((s) => s.user?.role) ?? null;

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['dashboard-summary'],
    queryFn: () => dashboardApi.summary(),
    refetchInterval: 60_000,
  });

  if (isError) {
    return (
      <Alert
        type="error"
        showIcon
        message="Could not load dashboard"
        description={
          <button
            type="button"
            onClick={() => refetch()}
            style={{ background: 'none', border: 'none', color: token.colorPrimary, cursor: 'pointer', padding: 0 }}
          >
            Try again
          </button>
        }
      />
    );
  }

  if (isLoading || !data) return <DashboardSkeleton />;

  switch (role) {
    case 'Accountant':         return <AccountantDashboard data={data} />;
    case 'Cashier':            return <CashierDashboard data={data} />;
    case 'Teacher':            return <TeacherDashboard data={data} />;
    case 'Admin / Front Desk': return <FrontDeskDashboard data={data} />;
    case 'HR Manager':         return <HrDashboard data={data} />;
    case 'Super Admin':
    case 'Principal':
    default:                   return <AdminDashboard data={data} />;
  }
}
